import cls from 'classnames';
import { FC } from 'react';
import { UseFormRegister } from 'react-hook-form';

import { LotteryFormData } from '@/lib/lottery2';

import { Field } from '..';

interface CheckboxProps {
  register: UseFormRegister<LotteryFormData>;
  field: Field;
  showAnswerAsText?: boolean;
}

interface FieldWithOptions extends Field {
  options: ReadonlyArray<{
    value: string;
    label: string;
  }>;
}

interface CheckboxGroupProps {
  register: UseFormRegister<LotteryFormData>;
  field: FieldWithOptions;
}

const checkboxStyle = cls(
  'w-6 h-6 text-red-medium rounded-[0.25rem] bg-black border-none cursor-pointer' +
    '!outline-1 focus:outline-gray-2 focus:outline-offset-1 !ring-transparent',
  'teair:bg-air-mediumBlue teair:bg-opacity-20 teair:checked:bg-opacity-100',
);

const labelStyle = 'grow cursor-pointer pl-2 select-none';

const sanitizeCheckboxRegisterSegment = (value: string) =>
  value.replaceAll(/[,[\].]/g, '');

const Checkbox: FC<CheckboxProps> = ({ register, field, showAnswerAsText }) => {
  const { name, label, required, pattern } = field;
  const value = label.toString().replaceAll(' ', '-');
  const checkboxName = showAnswerAsText
    ? `${name} checkbox_value: ${sanitizeCheckboxRegisterSegment(
        label.toString(),
      )}`
    : name;
  const checkboxId = showAnswerAsText ? `checkbox_${name}_${value}` : name;

  return (
    <div className="flex">
      <input
        {...register(checkboxName, {
          required,
          ...(pattern ? { pattern } : {}),
        })}
        type="checkbox"
        id={checkboxId}
        className={checkboxStyle}
      />
      <label htmlFor={checkboxId} className={labelStyle}>
        {label}
      </label>
    </div>
  );
};

const CheckboxGroup: FC<CheckboxGroupProps> = ({ register, field }) => {
  const { name, label, options } = field;

  return (
    <div className="flex flex-col">
      <label className="text-overline mb-2">{label}</label>

      {options.map((option) => {
        const value = option.value.replaceAll(' ', '-');
        const id = `checkbox_${name}_${value}`;

        return (
          <div key={value} className="flex py-2">
            <input
              {...register(
                `${name} checkbox_value: ${sanitizeCheckboxRegisterSegment(
                  option.value,
                )}`,
                {
                  required: false,
                },
              )}
              type="checkbox"
              id={id}
              className={checkboxStyle}
            />
            <label className={labelStyle} htmlFor={id}>
              {option.label}
            </label>
          </div>
        );
      })}
    </div>
  );
};

export { Checkbox, CheckboxGroup };
